// ============================================================================
//  ClaimDiagnoses — the Diagnoses table inside the inline claim editor
// ----------------------------------------------------------------------------
//  The same shape as the Procedures table, because it is the same job: Add a row,
//  fill it in, save it, Add the next. Differences are the ones the data forces:
//
//    ICD10       what you pick BY. The catalogue is searched on code or name —
//                "malaria", "J18.9", "diabetes" — because 829 codes is not a
//                list anyone scrolls.
//    Diagnosis   follows the ICD-10, so it is shown and not typed.
//    G-DRG       what the claim would be PRICED on. Comes from the priced
//                catalogue when it is loaded; blank from the ICD-10 reference
//                list, which carries none, and the header says so.
//    Principal   NHIA wants one principal diagnosis per claim. The importer sets
//                none — all 3,529 imported rows are 0 — so this is the only place
//                it gets set. At most one, enforced on the server too.
//
//  No date column: claim_diagnoses has no date. A diagnosis belongs to the visit,
//  not to a day within it, which is why procedures have one and these do not.
// ============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { cachedGet } from './catalogueCache';
//  Pencil, not Save, on the Edit button. It read as a floppy disk beside a red
//  X, so the row looked like "save or discard" rather than "change this one",
//  and officers went to Add and then hit the duplicate guard. ClaimMedications
//  has used the pencil for a while; these three had not caught up.
import { Save, Trash2, X, Loader2, Star, Pencil } from 'lucide-react';
import { claimStoreDiagnosisTariff, claimStoreDiagnosis } from '../BaseURL';
import SearchSelect from './SearchSelect';
import { confirmRemove } from './claimsDialog';

const BRAND = {
  green: '#1A9B7F', greenDark: '#137a63', greenSoft: '#e8f5f1',
  line: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkFaint: '#94a3b8',
  head: '#f1f5f9', danger: '#b91c1c', dangerSoft: '#fef2f2', amber: '#b45309',
  amberSoft: '#fffbeb',
};

const th = {
  textAlign: 'left', padding: '9px 10px', fontSize: 10.5, fontWeight: 800,
  letterSpacing: 0.4, textTransform: 'uppercase', color: BRAND.inkSoft,
  background: BRAND.head, borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px', fontSize: 12, color: BRAND.ink,
  borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle',
};
const iconBtn = (tone) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
  border: `1px solid ${tone === 'danger' ? '#fecaca' : BRAND.line}`,
  background: tone === 'danger' ? BRAND.dangerSoft : '#fff',
  color: tone === 'danger' ? BRAND.danger : BRAND.greenDark,
});

const blankRow = () => ({
  id: null, icd10_code: '', description: '', gdrg_code: '', tariff: null,
  is_principal: false,
});

export default function ClaimDiagnoses({
  claimId, source, scheme, age, rows = [], onSaved, readOnly,
  addSignal = false, onAddConsumed,
  //  'inpatient' | 'outpatient'. Decides whether the Principal column is live at
  //  all — see canBePrincipal below.
  setting,
  //  How many procedures the claim carries. A procedure outranks every diagnosis,
  //  so with one present the principal is chosen in the Procedures table and this
  //  column is disabled.
  procedureCount = 0,
}) {
  //  ── Can a DIAGNOSIS be the principal G-DRG on this claim? ─────────────────
  //  Only on an admission.
  //
  //  An out-patient attendance is billed on its SERVICE — the OPDC code at the top
  //  of the claim — or on a procedure, when one was done. It is never billed on a
  //  diagnosis, so "the principal diagnosis" is not a thing an OPD claim has, and
  //  offering a star to set one was offering a choice that does not exist.
  //
  //  Worse than useless: an officer who ticked it produced a claim whose stored
  //  principal contradicts what the claim is actually billed on, and the amber
  //  "No principal diagnosis is set on this claim. Use the star to mark one."
  //  prompt below was actively telling them to do it.
  //
  //  ── AND ONLY WHEN THERE IS NO PROCEDURE ───────────────────────────────────
  //
  //  resolvePrincipal takes a procedure ahead of ANY diagnosis, flagged or not. So
  //  on a claim that carries one, starring a diagnosis changes a stored flag and
  //  nothing else — the claim stays billed on the procedure, and the star sits
  //  there filled in, contradicting the Service shown at the top of the editor.
  //
  //  The Procedures table now has its own star for exactly this reason. With a
  //  procedure present the choice belongs there, so this column goes dead rather
  //  than offering a second, losing answer to the same question.
  //
  //  Defaults to allowed when `setting` is absent, so a caller that has not been
  //  updated keeps the old behaviour rather than silently losing the column.
  const hasProcedure = procedureCount > 0;
  const canBePrincipal = setting !== 'outpatient' && !hasProcedure;
  const principalOffReason = hasProcedure
    ? 'This claim is billed on its procedure, so the principal is chosen in the '
      + 'Procedures table above. A diagnosis is only the principal when there is no '
      + 'procedure.'
    : 'An out-patient claim is billed on its service — or on a procedure, if one was '
      + 'done — never on a diagnosis. Only an admission has a principal diagnosis.';
  const [catalogue, setCatalogue] = useState([]);
  const [catNote, setCatNote] = useState(null);
  const [priced, setPriced] = useState(true);
  //  Which age band the list has been narrowed to. Shown, because a NARROWED list
  //  and a SHORT list look identical — an officer who cannot find MEDI31 on a
  //  four-year-old's claim needs to be told it was withheld on purpose, not left
  //  to conclude the catalogue is incomplete.
  const [catBand, setCatBand] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    cachedGet(claimStoreDiagnosisTariff, { scheme, age })
      .then((data) => {
        if (!alive) return;
        setCatalogue(data?.options || []);
        setCatNote(data?.note || null);
        setPriced(data?.priced !== false);
        setCatBand(data?.band || null);
      })
      .catch(() => {
        if (!alive) return;
        setCatalogue([]);
        setCatNote('The diagnosis list could not be loaded.');
        setCatBand(null);
      });
    return () => { alive = false; };
  }, [scheme, age]);

  const addRow = useCallback(() => {
    setError(null);
    setDrafts((d) => [...d, {
      key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ...blankRow(),
    }]);
  }, []);

  //  One-shot flag from the fold's Add button — see the note in ClaimProcedures:
  //  the fold mounts this on the same click, so there is no earlier value.
  useEffect(() => {
    if (!addSignal) return;
    if (!readOnly) addRow();
    if (onAddConsumed) onAddConsumed();
  }, [addSignal, addRow, readOnly, onAddConsumed]);

  const patch = (key, k, v) => setDrafts((d) =>
    d.map((x) => (x.key === key ? { ...x, [k]: v } : x)));

  //  Picking brings the ICD-10, the name, the G-DRG and the tariff together. The
  //  OPTION is used rather than a lookup by code: the same ICD-10 appears under
  //  several G-DRGs, so a code alone does not identify which row was chosen.
  //  Already on the claim? One diagnosis per ICD-10 — the G-DRG is deliberately not
  //  part of this, because the same condition billed under two G-DRGs is still one
  //  diagnosis. Told at the moment of picking; the server refuses it too.
  //  ── A ROW IS NOT A DUPLICATE OF ITSELF ──────────────────────────────────
  //
  //  REPORTED: opening a saved diagnosis to change it answered "Peptic Ulcer
  //  Disease Helicobactor Pylori Infection (K27) is already on this claim" - and
  //  it was, in the very row being edited. The check compared the draft against
  //  every SAVED row including its own, so an edited row could never be saved
  //  unless its ICD-10 was changed to something not on the claim. Re-picking the
  //  same diagnosis, or correcting only the G-DRG, was refused outright.
  //
  //  `selfId` is the saved row this draft came from - editRow spreads the row in,
  //  so an edited draft carries its id and a newly added one does not. The same
  //  rule ClaimMedications.duplicateOf already used.
  const duplicateOf = (key, icd, selfId) => {
    const same = (a, b) => String(a || '').toUpperCase() === String(b || '').toUpperCase();
    const isSelf = (r) => selfId != null && String(r.id) === String(selfId);
    if (rows.some((r) => !isSelf(r) && same(r.icd10_code, icd))) return 'saved';
    return drafts.some((x) => x.key !== key && same(x.icd10_code, icd)) ? 'draft' : null;
  };

  const pickDiagnosis = (key, _id, opt) => {
    if (!opt) return;
    const icd = opt.icd10 || opt.code || '';
    //  The draft's own id, so substituting a diagnosis on a saved row is not
    //  reported as a clash with the row it is replacing.
    const selfId = (drafts.find((x) => x.key === key) || {}).id;
    const dup = duplicateOf(key, icd, selfId);
    setError(dup
      ? `${opt.name || icd} (${icd}) is already on this claim`
        + `${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`
      : null);
    setDrafts((d) => d.map((x) => (x.key === key ? {
      ...x,
      icd10_code: icd,
      description: opt.name || '',
      gdrg_code: opt.gdrg || '',
      tariff: opt.tariff ?? null,
      duplicate: Boolean(dup),
    } : x)));
  };

  //  The identity SearchSelect compares on, and the value a draft row presents
  //  back to it. Must be built the same way in both places or the trigger shows
  //  a different row than the one that was picked.
  //
  //  THE NAME IS PART OF IT. 88 of the 594 offered NHIA rows share an
  //  (ICD-10, G-DRG) pair with another row — "Acute Cystitis" and "Cystitis" are
  //  both N30.00/MEDI31A — so on those the trigger showed whichever came first in
  //  the catalogue rather than the row on the claim. Trimmed and upper-cased only:
  //  A08.2/MEDI23A is "Bacteria Enteritis" and "Bacteria Enteritis." at two
  //  different tariffs, and the full stop is the whole difference.
  const nameKey = (v) => String(v || '').trim().toUpperCase();
  const idOf = (o) => `${o.icd10 || o.code}::${o.gdrg || ''}::${nameKey(o.name)}`;
  const valueOf = (row) => `${row.icd10_code || ''}::${row.gdrg_code || ''}`
    + `::${nameKey(row.description)}`;

  //  The claim's own diagnosis, offered when the catalogue does not contain its
  //  (ICD-10, G-DRG, name) triple. The same fault the medications table had:
  //  editing a saved row opened with an empty picker, so changing the principal
  //  meant finding the diagnosis again.
  const optionsWith = (row) => {
    if (!row.icd10_code) return catalogue;
    const want = valueOf(row);
    if (catalogue.some((o) => idOf(o) === want)) return catalogue;
    //  `name` is the row's description VERBATIM, not `|| row.icd10_code`. The name
    //  is part of the identity now, so falling back to the code here would make
    //  idOf(this option) end in the code while valueOf(row) ends in nothing — the
    //  option would be present and never selectable, which is the empty-picker bug
    //  this function exists to prevent. The code is shown beside the name by the
    //  row label, so an unnamed row still reads.
    return [{
      id: want, icd10: row.icd10_code, code: row.icd10_code,
      name: row.description || '', gdrg: row.gdrg_code || null,
      tariff: row.tariff ?? null, specialty_name: null,
      variants: 1, variantIndex: 1, onThisClaim: true,
    }, ...catalogue];
  };

  const drop = (key) => setDrafts((d) => d.filter((x) => x.key !== key));

  const editRow = (r) => {
    setError(null);
    setDrafts((d) => (d.some((x) => x.id === r.id)
      ? d
      : [...d, { key: `row-${r.id}`, ...r, is_principal: !!r.is_principal }]));
  };

  const save = async (row) => {
    if (!row.icd10_code) { setError('Pick a diagnosis first.'); return; }
    const dup = duplicateOf(row.key, row.icd10_code, row.id);
    if (dup) {
      setError(`${row.description || row.icd10_code} (${row.icd10_code}) is already `
        + `on this claim${dup === 'draft' ? ' — in a row above, not yet saved' : ''}.`);
      return;
    }
    setBusy(row.key); setError(null);
    try {
      const { data } = await axios[row.id ? 'put' : 'post'](claimStoreDiagnosis, {
        id: row.id || undefined,
        icd10_code: row.icd10_code,
        description: row.description,
        gdrg_code: row.gdrg_code || null,
        tariff: row.tariff,
        is_principal: !!row.is_principal,
      }, { params: { id: claimId, source } });
      drop(row.key);
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save the diagnosis.');
    } finally { setBusy(null); }
  };

  const remove = async (r) => {
    if (!(await confirmRemove({ what: 'diagnosis', name: r.description || r.icd10_code }))) return;
    setBusy(`row-${r.id}`); setError(null);
    try {
      const { data } = await axios.delete(claimStoreDiagnosis,
        { params: { id: claimId, source, rowId: r.id } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not remove the diagnosis.');
    } finally { setBusy(null); }
  };

  //  Promote an existing row to principal without opening it for editing — the
  //  common case is "this one, not that one", and it is one click.
  const makePrincipal = async (r) => {
    setBusy(`row-${r.id}`); setError(null);
    try {
      const { data } = await axios.put(claimStoreDiagnosis, {
        id: r.id, icd10_code: r.icd10_code, description: r.description,
        gdrg_code: r.gdrg_code || null, tariff: r.tariff, is_principal: true,
      }, { params: { id: claimId, source } });
      if (data && onSaved) onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not set the principal.');
    } finally { setBusy(null); }
  };

  //  One draft row. A FUNCTION rather than inline JSX because it renders in
  //  two places: a new row at the top of the table, an edited row in the
  //  position of the row it replaces. Clicking Edit must not move anything.
  const draftTr = (row, i) => (
                <tr key={row.key} style={{
                  background: row.duplicate ? BRAND.dangerSoft : BRAND.greenSoft }}>
                  <td style={{ ...td, color: BRAND.inkFaint }}>{'\u2014'}</td>
                  {/*  Searched, not scrolled: 829 ICD-10 codes. */}
                  <td style={td}>
                    <SearchSelect
                      options={optionsWith(row)}
                      value={valueOf(row)}
                      idOf={idOf}
                      onChange={(id, opt) => pickDiagnosis(row.key, id, opt)}
                      placeholder="Select diagnosis"
                      searchPlaceholder="Type a diagnosis or ICD-10 code…"
                      //  A row ADDED opens its list with the cursor already in the
                      //  search box. `!row.id` is what distinguishes it from a
                      //  saved row opened for editing — that one must not grab the
                      //  cursor, because you usually opened it to change something
                      //  other than the diagnosis itself.
                      autoOpen={!row.id}
                      ariaLabel="ICD10"
                      //  The closed field shows the CODE, because it sits in the
                      //  ICD10 column and that heading is a promise. Row 1 of this
                      //  same table reads "J18.9"; a draft row that read
                      //  "Abdominal injury due to birth trauma" under ICD10 — with
                      //  the name repeated in the next column — was the bug.
                      triggerOf={(o) => (
                        <strong style={{ fontWeight: 800 }}>{o.icd10 || o.code}</strong>
                      )}
                      //  The LIST rows lead with the name. The same ICD-10 is
                      //  billable under several G-DRGs — Endometriosis under five —
                      //  so leading with the code there makes five real choices look
                      //  like one repeated five times.
                      primaryOf={(o) => (
                        <span>
                          {o.name}
                          {o.variants > 1 && (
                            <span style={{
                              marginLeft: 6, fontSize: 9.5, fontWeight: 800,
                              padding: '1px 5px', borderRadius: 4,
                              background: BRAND.head, color: BRAND.inkSoft,
                            }}>{o.variantIndex} of {o.variants} G-DRGs</span>
                          )}
                          {/*  ── SHOWN, BUT NOT THIS PATIENT'S BAND ───────────
                               Every registered diagnosis is offered now — they
                               used to be dropped, so a diagnosis just added to
                               the setup screen was simply absent with nothing
                               saying why. The ones that do not match the
                               patient's age sort last and carry this, so a
                               paediatric tariff cannot be put on an adult
                               without the officer seeing it happen. */}
                          {o.out_of_band && (
                            <span
                              title={'This is the tariff for the other age band. It is '
                                + 'shown so you can use it if the date of birth is the '
                                + 'thing that is wrong — but NHIA prices on the band.'}
                              style={{
                                marginLeft: 6, fontSize: 9.5, fontWeight: 800,
                                padding: '1px 5px', borderRadius: 4,
                                background: '#FFF8EC', color: '#8A5510',
                                border: '1px solid rgba(224,139,47,.35)',
                              }}>OTHER AGE BAND</span>
                          )}
                        </span>
                      )}
                      //  The code, the G-DRG and the specialty — what actually tells
                      //  two rows of the same diagnosis apart. The tariff is shown
                      //  only when there IS one: every row of diagnosis_tarf is
                      //  currently 0, and "GHS 0.00" on all 694 is noise.
                      secondaryOf={(o) => [
                        o.icd10 || o.code,
                        o.gdrg ? `G-DRG ${o.gdrg}` : null,
                        o.specialty_name || null,
                        o.tariff ? `GHS ${Number(o.tariff).toFixed(2)}` : null,
                        o.icd_chapter ? `ICD chapter ${o.icd_chapter}` : null,
                      ].filter(Boolean).join('  ·  ')} />
                  </td>
                  <td style={{ ...td, color: BRAND.inkSoft }}>
                    {row.description || <span style={{ color: BRAND.inkFaint }}>
                      Pick a diagnosis
                    </span>}
                  </td>
                  <td style={{ ...td, color: BRAND.inkSoft }}>
                    {row.gdrg_code || <span style={{ color: BRAND.inkFaint }}>—</span>}
                  </td>
                  <td style={td}>
                    {/*  Same rule as the saved rows: no principal to set on an
                        out-patient claim, so the box is not offered. A dash rather
                        than an empty cell, so the column still reads as a column and
                        the row does not look half-rendered. */}
                    {canBePrincipal ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5,
                        fontSize: 11, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!row.is_principal}
                          aria-label="Principal diagnosis"
                          onChange={(e) => patch(row.key, 'is_principal', e.target.checked)}
                          style={{ accentColor: BRAND.green, width: 14, height: 14 }} />
                      </label>
                    ) : (
                      <span title={principalOffReason}
                        style={{ color: BRAND.inkFaint, fontSize: 11 }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button" title="Save" style={iconBtn()}
                        aria-label="Save diagnosis"
                        disabled={busy === row.key}
                        onClick={() => save(row)}>
                        {busy === row.key
                          ? <Loader2 size={13} className="spin" />
                          : <Save size={13} />}
                      </button>
                      <button type="button" title="Discard" style={iconBtn('danger')}
                        aria-label="Discard row"
                        onClick={() => drop(row.key)}><X size={13} /></button>
                    </div>
                  </td>
                </tr>
  );

  //  New rows only — an edited row is drawn where it already sits.
  const newDrafts = drafts.filter((d) => !d.id);
  const draftFor = (id) => drafts.find((d) => d.id != null && d.id === id);

  //  NOT date-sorted, unlike the other three tables: claim_diagnoses has no date.
  //  A diagnosis belongs to the visit rather than to a day within it, so the order
  //  the server returns is the only order there is. Drafts still appear at the top,
  //  which is the half of the fix that matters here.
  const saved = rows;
  const isEmpty = saved.length === 0 && drafts.length === 0;
  //  Only ever asked of an ADMISSION. On an out-patient claim this prompt was
  //  telling the officer to set something that cannot exist — "Use the star to mark
  //  one" beside a column that is now disabled — which is how an OPD diagnosis came
  //  to be starred in the first place.
  const noPrincipal = canBePrincipal
    && saved.length > 0 && !saved.some((r) => r.is_principal);

  return (
    <div>
      {/*  No Add here — there is one, in the fold's header. */}
      {(catNote || noPrincipal || catBand) && (
        <div style={{ display: 'grid', gap: 5, marginBottom: 8 }}>
          {/*  WHY THE LIST IS SHORT. The same ICD-10 is often billable under an
               adult G-DRG and a child one — N39.0 under MEDI31 and PAED39 — and
               only the one matching this patient is offered. Without this line a
               narrowed list is indistinguishable from a broken one, and the
               officer goes looking for a code that was withheld on purpose.  */}
          {catBand && (
            <div style={{
              fontSize: 10.5, padding: '6px 9px', borderRadius: 6,
              background: '#EEF4FB', color: '#2A5480', border: '1px solid #D6E4F5',
            }}>
              {/*  REWRITTEN when the band stopped being a filter. It used to end
                   "is deliberately not listed", which is no longer true — every
                   registered diagnosis is offered now, and a note that describes
                   behaviour the screen no longer has is worse than none. */}
              This patient is {catBand === 'child' ? 'a CHILD' : 'an ADULT'}, so
              {' '}{catBand} diagnoses and those billable to both come first. The
              {catBand === 'child' ? ' adult' : ' paediatric'} tariff for the same
              condition is further down, marked <b>OTHER AGE BAND</b> — usable, but
              NHIA prices on the band, so pick it only if the date of birth is what
              is wrong.
            </div>
          )}
          {catNote && (
            <div style={{
              fontSize: 10.5, padding: '6px 9px', borderRadius: 6,
              background: priced ? 'transparent' : BRAND.amberSoft,
              color: priced ? BRAND.inkFaint : BRAND.amber,
              border: priced ? 0 : '1px solid #fde68a',
            }}>{catNote}</div>
          )}
          {noPrincipal && (
            //  NHIA wants one principal per claim and the importer set none, so
            //  this is worth saying rather than leaving to be discovered at export.
            <div style={{
              fontSize: 10.5, padding: '6px 9px', borderRadius: 6,
              background: BRAND.amberSoft, color: BRAND.amber,
              border: '1px solid #fde68a',
            }}>
              No principal diagnosis is set on this claim. Use the star to mark one.
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{
          marginBottom: 8, padding: '7px 10px', borderRadius: 7, fontSize: 11.5,
          background: BRAND.dangerSoft, color: BRAND.danger,
          border: '1px solid #fecaca',
        }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>#</th>
              <th style={{ ...th, width: 190 }}>ICD10</th>
              <th style={th}>Diagnosis</th>
              <th style={{ ...th, width: 96 }}>G-DRG</th>
              <th style={{ ...th, width: 76 }}>Principal</th>
              <th style={{ ...th, width: 76 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/*  A duplicate draft row turns red, so the message above names which
                 row it is talking about. */}
            {newDrafts.map(draftTr)}

            {isEmpty && (
              <tr><td colSpan={6} style={{
                ...td, textAlign: 'center', color: BRAND.inkFaint, padding: '22px 10px',
              }}>No diagnoses available</td></tr>
            )}

            {saved.map((r, i) => {
              //  Being edited? Draw the draft HERE, in this row's place.
              const d = draftFor(r.id);
              if (d) return draftTr(d, i);
              return (
              <tr key={r.id}>
                <td style={{ ...td, color: BRAND.inkFaint }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 700 }}>{r.icd10_code || '—'}</td>
                <td style={td}>{r.description}</td>
                <td style={td}>{r.gdrg_code || '—'}</td>
                <td style={td}>
                  <button type="button"
                    title={!canBePrincipal ? principalOffReason
                      : r.is_principal ? 'The principal diagnosis'
                        : 'Make this the principal diagnosis'}
                    aria-label={!canBePrincipal
                      ? `Principal diagnosis does not apply to an out-patient claim`
                      : r.is_principal
                        ? `${r.description} is the principal diagnosis`
                        : `Make ${r.description} the principal diagnosis`}
                    //  Disabled outright on an out-patient claim. `disabled` and not
                    //  merely an onClick guard, so the keyboard cannot reach it
                    //  either and a screen reader announces it as unavailable.
                    disabled={!canBePrincipal || readOnly || busy === `row-${r.id}`}
                    onClick={() => canBePrincipal && !r.is_principal && makePrincipal(r)}
                    style={{
                      border: 0, background: 'none', padding: 2, display: 'flex',
                      cursor: !canBePrincipal || readOnly || r.is_principal
                        ? 'default' : 'pointer',
                      //  A faint outline, not the green. On an OPD claim the star has
                      //  to read as "not applicable" rather than as "not yet set" —
                      //  including when the stored flag IS set, which happens on
                      //  claims starred before this was disabled. Showing those green
                      //  would present a meaningless flag as a deliberate choice.
                      color: !canBePrincipal ? BRAND.line
                        : r.is_principal ? BRAND.green : BRAND.line,
                      opacity: canBePrincipal ? 1 : 0.45,
                    }}>
                    <Star size={16}
                      fill={canBePrincipal && r.is_principal ? BRAND.green : 'none'} />
                  </button>
                </td>
                <td style={td}>
                  {!readOnly && (
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button"
                        title="Change this diagnosis — pick another and save"
                        style={iconBtn()}
                        aria-label={`Edit ${r.description || r.icd10_code}`}
                        onClick={() => editRow(r)}><Pencil size={13} /></button>
                      <button type="button" title="Remove" style={iconBtn('danger')}
                        aria-label={`Remove ${r.description || r.icd10_code}`}
                        onClick={() => remove(r)}><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}

          </tbody>
        </table>
      </div>
    </div>
  );
}
